import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { RefreshCw, CheckCircle2, XCircle, AlertCircle, Link2, Unlink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { pipefyApi } from "@/lib/api-client";
import { useSupabase } from "@/hooks/useSupabase";

interface SyncResult {
  centrosCusto: {
    imported: number;
    errors: string[];
  };
  tiposDespesa: {
    imported: number;
    errors: string[];
  };
}

type CentroPlanilha = { nome: string; count: number };
type ParAreaSubArea = { area: string; sub_area: string; count: number };
type Mapeamento = { nome_planilha?: string | null; area?: string | null; sub_area?: string | null; centro_custo_id: string };
type CentroCusto = { id: string; nome: string; nivel: string | null };

function keyAreaSubArea(area: string, sub_area: string): string {
  const sub = (sub_area ?? "").trim() || "–";
  return `${(area ?? "").trim()}|${sub}`;
}

export default function SincronizarPipefyPage() {
  const { supabase } = useSupabase();
  const [centroCustoId, setCentroCustoId] = useState("306546622");
  const [tipoDespesaId, setTipoDespesaId] = useState("306808314");
  const [orcamentoDatabaseId, setOrcamentoDatabaseId] = useState("306808641");
  const [loading, setLoading] = useState(false);
  const [loadingOrcamento, setLoadingOrcamento] = useState(false);
  const [result, setResult] = useState<SyncResult | null>(null);
  const [orcamentoResult, setOrcamentoResult] = useState<any>(null);
  const [viewOrcamentoCount, setViewOrcamentoCount] = useState<number | null>(null);
  const [allowCreateOrcamento, setAllowCreateOrcamento] = useState(false);
  const [tiposDatabaseSyncId, setTiposDatabaseSyncId] = useState("306808314");
  const [tiposAno, setTiposAno] = useState(new Date().getFullYear().toString());
  const [allowCreateTipos, setAllowCreateTipos] = useState(true);
  const [allowDeactivateTipos, setAllowDeactivateTipos] = useState(false);
  const [loadingTipos, setLoadingTipos] = useState(false);
  const [loadingTiposRealizado, setLoadingTiposRealizado] = useState(false);
  const [tiposResult, setTiposResult] = useState<any>(null);

  // Vincular centros de custo (planilha → Pipefy): por (area, sub_area) e legado por centro_resultado
  const [paresAreaSubArea, setParesAreaSubArea] = useState<ParAreaSubArea[]>([]);
  const [centrosPlanilha, setCentrosPlanilha] = useState<CentroPlanilha[]>([]);
  const [mapeamentos, setMapeamentos] = useState<Mapeamento[]>([]);
  const [centrosCusto, setCentrosCusto] = useState<CentroCusto[]>([]);
  const [selectedMap, setSelectedMap] = useState<Record<string, string>>({});
  const [selectedMapLegado, setSelectedMapLegado] = useState<Record<string, string>>({});
  const [loadingVinculos, setLoadingVinculos] = useState(false);
  const [applyingRpc, setApplyingRpc] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [removingKey, setRemovingKey] = useState<string | null>(null);


  const handleSync = async () => {
    if (!centroCustoId || !tipoDespesaId) {
      toast.error("Por favor, preencha os IDs dos databases");
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const data = await pipefyApi.syncDatabases({
        centroCustoDatabaseId: centroCustoId.trim(),
        tipoDespesaDatabaseId: tipoDespesaId.trim(),
      });

      setResult(data.results);
      toast.success(data.message || "Sincronização concluída!");
    } catch (error: any) {
      console.error("Erro:", error);
      toast.error(error.message || "Erro ao sincronizar databases");
    } finally {
      setLoading(false);
    }
  };

  const handleSyncOrcamentoData = async () => {
    if (!orcamentoDatabaseId || !centroCustoId || !tipoDespesaId) {
      toast.error("Por favor, preencha todos os IDs necessários");
      return;
    }

    setLoadingOrcamento(true);

    try {
      const BATCH = 12;
      let offset = 0;
      let totalCreated = 0;
      let totalUpdated = 0;
      let totalSkipped = 0;
      let totalErr = 0;
      const allErrors: unknown[] = [];
      let last: Record<string, unknown> | null = null;

      while (true) {
        const data = (await pipefyApi.syncOrcamento({
          orcamentoDatabaseId: orcamentoDatabaseId.trim(),
          sync: true,
          allowCreate: allowCreateOrcamento,
          mode: "v2",
          centroCustoDatabaseId: centroCustoId.trim(),
          tipoDespesaDatabaseId: tipoDespesaId.trim(),
          batchOffset: offset,
          batchSize: BATCH,
        })) as Record<string, unknown>;

        last = data;
        totalCreated += Number(data.created ?? 0);
        totalUpdated += Number(data.updated ?? 0);
        totalSkipped += Number(data.skipped ?? 0);
        totalErr += Number(data.totalErrors ?? 0);
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          allErrors.push(...data.errors);
        }

        const totalInView = Number(data.totalRecordsInView ?? 0);
        const processedThis = Number(data.batchSizeProcessed ?? BATCH);
        const next = data.nextBatchOffset;
        const hasMore =
          data.hasMore === false
            ? false
            : data.hasMore === true ||
              (typeof next === "number" && next < totalInView && processedThis > 0);
        if (!hasMore) break;
        offset = typeof next === "number" ? next : offset + processedThis;
        toast.loading(
          `Sincronizando orçamento… lote ${offset}/${String(data.totalRecordsInView ?? "?")}`,
          { id: "orcamento-sync-batch" }
        );
      }

      toast.dismiss("orcamento-sync-batch");

      const merged = last
        ? {
            ...last,
            created: totalCreated,
            updated: totalUpdated,
            skipped: totalSkipped,
            totalErrors: totalErr,
            errors: allErrors,
            synced: totalCreated + totalUpdated,
            message: `Sincronização concluída: ${totalCreated} criados, ${totalUpdated} atualizados (${totalErr} erros em campos)`,
          }
        : null;

      setOrcamentoResult(merged);
      toast.success(
        merged?.message ??
          `Sincronização concluída: ${totalCreated + totalUpdated} registros processados`
      );

      if (allErrors.length > 0) {
        console.warn("Erros durante sincronização:", allErrors);
      }
    } catch (error: any) {
      console.error("Erro:", error);
      toast.error(error.message || "Erro ao sincronizar dados");
    } finally {
      toast.dismiss("orcamento-sync-batch");
      setLoadingOrcamento(false);
    }
  };

  const handleSyncTipos = async () => {
    if (!tiposDatabaseSyncId.trim()) {
      toast.error("Informe o ID do database de Tipos de Despesa");
      return;
    }
    if (!tiposAno.trim() || Number.isNaN(Number(tiposAno))) {
      toast.error("Informe um ano válido");
      return;
    }

    setLoadingTipos(true);
    setTiposResult(null);
    try {
      const data = await pipefyApi.syncTipos({
        tiposDatabaseId: tiposDatabaseSyncId.trim(),
        ano: Number(tiposAno),
        allowCreate: allowCreateTipos,
        allowDeactivate: allowDeactivateTipos,
        sourceMode: "orcamento",
      });
      setTiposResult(data);
      toast.success("Sincronização de tipos concluída");
    } catch (error: any) {
      console.error("Erro:", error);
      toast.error(error.message || "Erro ao sincronizar tipos de despesa");
    } finally {
      setLoadingTipos(false);
    }
  };

  const handleSyncTiposRealizadoSemOrcamento = async () => {
    if (!tiposDatabaseSyncId.trim()) {
      toast.error("Informe o ID do database de Tipos de Despesa");
      return;
    }
    if (!tiposAno.trim() || Number.isNaN(Number(tiposAno))) {
      toast.error("Informe um ano válido");
      return;
    }

    setLoadingTiposRealizado(true);
    setTiposResult(null);
    try {
      const data = await pipefyApi.syncTipos({
        tiposDatabaseId: tiposDatabaseSyncId.trim(),
        ano: Number(tiposAno),
        allowCreate: allowCreateTipos,
        // Para esse modo, não queremos desativar tipos da base inteira.
        allowDeactivate: false,
        sourceMode: "realizado_sem_orcamento",
      });
      setTiposResult(data);
      toast.success("Sincronização de tipos (realizado sem orçamento) concluída");
    } catch (error: any) {
      console.error("Erro:", error);
      toast.error(error.message || "Erro ao sincronizar tipos do realizado sem orçamento");
    } finally {
      setLoadingTiposRealizado(false);
    }
  };

  const loadVinculos = useCallback(async () => {
    setLoadingVinculos(true);
    try {
      const [mapeamentosRes, centrosRes] = await Promise.all([
        supabase.from("solis_centro_custo_mapeamento").select("nome_planilha, area, sub_area, centro_custo_id"),
        supabase.from("solis_centros_custo").select("id, nome, nivel").eq("ativo", true).order("nome"),
      ]);
      if (mapeamentosRes.error) throw mapeamentosRes.error;
      if (centrosRes.error) throw centrosRes.error;
      setMapeamentos(mapeamentosRes.data || []);
      setCentrosCusto(centrosRes.data || []);

      const countByAreaSub: Record<string, { area: string; sub_area: string; count: number }> = {};
      const countByCentro: Record<string, number> = {};
      let from = 0;
      const pageSize = 1000;
      let hasMore = true;
      while (hasMore) {
        const { data, error } = await supabase
          .from("solis_orcamento")
          .select("area, sub_area, centro_resultado")
          .range(from, from + pageSize - 1);
        if (error) throw error;
        const list = data || [];
        list.forEach((r: { area: string | null; sub_area: string | null; centro_resultado: string | null }) => {
          const area = (r.area ?? "").trim();
          const sub = (r.sub_area ?? "").trim() || "–";
          if (area) {
            const k = keyAreaSubArea(area, r.sub_area ?? "");
            if (!countByAreaSub[k]) countByAreaSub[k] = { area, sub_area: sub, count: 0 };
            countByAreaSub[k].count += 1;
          }
          const nome = (r.centro_resultado || "").trim() || "(vazio)";
          if (nome !== "(vazio)") countByCentro[nome] = (countByCentro[nome] || 0) + 1;
        });
        hasMore = list.length === pageSize;
        from += pageSize;
      }
      setParesAreaSubArea(
        Object.values(countByAreaSub).sort((a, b) => a.area.localeCompare(b.area) || a.sub_area.localeCompare(b.sub_area))
      );
      setCentrosPlanilha(
        Object.entries(countByCentro).map(([nome, count]) => ({ nome, count })).sort((a, b) => a.nome.localeCompare(b.nome))
      );

      const initial: Record<string, string> = {};
      const initialLegado: Record<string, string> = {};
      (mapeamentosRes.data || []).forEach((m: Mapeamento) => {
        if (m.area != null && m.sub_area != null) {
          initial[keyAreaSubArea(m.area, m.sub_area)] = m.centro_custo_id;
        } else if (m.nome_planilha) {
          initialLegado[m.nome_planilha] = m.centro_custo_id;
        }
      });
      setSelectedMap(initial);
      setSelectedMapLegado(initialLegado);
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || "Erro ao carregar dados para vínculos");
    } finally {
      setLoadingVinculos(false);
    }
  }, [supabase]);

  useEffect(() => {
    loadVinculos();
  }, [loadVinculos]);

  // Contagem de registros na view de orçamento (para exibir antes do sync)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { count, error } = await supabase
        .from("solis_vw_orcamento_por_subtipo_centro_com_chave_v2")
        .select("*", { count: "exact", head: true });
      if (!cancelled && !error && count != null) setViewOrcamentoCount(count);
    })();
    return () => { cancelled = true; };
  }, [supabase]);

  const saveMappingArea = async (area: string, sub_area: string, centroCustoId: string) => {
    const sub = (sub_area ?? "").trim() || "–";
    const key = keyAreaSubArea(area, sub_area);
    setSavingKey(key);
    try {
      const { error } = await supabase.from("solis_centro_custo_mapeamento").upsert(
        { area: area.trim(), sub_area: sub, centro_custo_id: centroCustoId },
        { onConflict: "area,sub_area" }
      );
      if (error) throw error;
      setMapeamentos((prev) => {
        const next = prev.filter((m) => keyAreaSubArea(m.area ?? "", m.sub_area ?? "") !== key);
        next.push({ area: area.trim(), sub_area: sub, centro_custo_id: centroCustoId });
        return next;
      });
      setSelectedMap((prev) => ({ ...prev, [key]: centroCustoId }));
      toast.success("Vínculo salvo");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar vínculo");
    } finally {
      setSavingKey(null);
    }
  };

  const removeMappingArea = async (area: string, sub_area: string) => {
    const sub = (sub_area ?? "").trim() || "–";
    const key = keyAreaSubArea(area, sub_area);
    setRemovingKey(key);
    try {
      const { error } = await supabase
        .from("solis_centro_custo_mapeamento")
        .delete()
        .eq("area", area.trim())
        .eq("sub_area", sub);
      if (error) throw error;
      setMapeamentos((prev) => prev.filter((m) => keyAreaSubArea(m.area ?? "", m.sub_area ?? "") !== key));
      setSelectedMap((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      toast.success("Vínculo removido");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao remover vínculo");
    } finally {
      setRemovingKey(null);
    }
  };

  const saveMappingLegado = async (nomePlanilha: string, centroCustoId: string) => {
    setSavingKey("legado:" + nomePlanilha);
    try {
      const { error } = await supabase.from("solis_centro_custo_mapeamento").upsert(
        { nome_planilha: nomePlanilha, centro_custo_id: centroCustoId },
        { onConflict: "nome_planilha" }
      );
      if (error) throw error;
      setMapeamentos((prev) => {
        const next = prev.filter((m) => m.nome_planilha !== nomePlanilha);
        next.push({ nome_planilha: nomePlanilha, centro_custo_id: centroCustoId });
        return next;
      });
      setSelectedMapLegado((prev) => ({ ...prev, [nomePlanilha]: centroCustoId }));
      toast.success(`"${nomePlanilha}" vinculado com sucesso`);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar vínculo");
    } finally {
      setSavingKey(null);
    }
  };

  const removeMappingLegado = async (nomePlanilha: string) => {
    setRemovingKey("legado:" + nomePlanilha);
    try {
      const { error } = await supabase
        .from("solis_centro_custo_mapeamento")
        .delete()
        .eq("nome_planilha", nomePlanilha);
      if (error) throw error;
      setMapeamentos((prev) => prev.filter((m) => m.nome_planilha !== nomePlanilha));
      setSelectedMapLegado((prev) => {
        const next = { ...prev };
        delete next[nomePlanilha];
        return next;
      });
      toast.success(`Vínculo de "${nomePlanilha}" removido`);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao remover vínculo");
    } finally {
      setRemovingKey(null);
    }
  };

  const applyVinculosToOrcamento = async () => {
    setApplyingRpc(true);
    try {
      const { error } = await supabase.rpc("atualizar_relacionamentos_orcamento");
      if (error) throw error;
      toast.success("Vínculos aplicados ao orçamento. centro_custo_id e tipo_despesa_id atualizados.");
      loadVinculos();
    } catch (e: any) {
      toast.error(e?.message || "Erro ao aplicar vínculos ao orçamento");
    } finally {
      setApplyingRpc(false);
    }
  };

  const getVinculadoArea = (area: string, sub_area: string): string | null => {
    const key = keyAreaSubArea(area, sub_area);
    const id = mapeamentos.find((m) => m.area != null && m.sub_area != null && keyAreaSubArea(m.area, m.sub_area) === key)?.centro_custo_id;
    if (!id) return null;
    return centrosCusto.find((c) => c.id === id)?.nome ?? null;
  };

  const getVinculadoNome = (nomePlanilha: string): string | null => {
    const id = mapeamentos.find((m) => m.nome_planilha === nomePlanilha)?.centro_custo_id;
    if (!id) return null;
    return centrosCusto.find((c) => c.id === id)?.nome ?? null;
  };

  return (
    <div className="space-y-4 p-6 max-w-4xl w-full mx-auto">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Sincronizar com Pipefy</h2>
        <p className="text-muted-foreground text-sm">
          Sincronize os databases do Pipefy (Centros de Custo e Tipos de Despesa) com o Supabase.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Configuração</CardTitle>
          <CardDescription>
            Informe os IDs dos databases do Pipefy. Você pode encontrá-los na URL do database ou nas configurações.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="centro-custo-id">ID do Database - Centros de Custo</Label>
            <Input
              id="centro-custo-id"
              placeholder="Ex: 301234567"
              value={centroCustoId}
              onChange={(e) => setCentroCustoId(e.target.value)}
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground">
              O ID do database de Centros de Custo no Pipefy
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tipo-despesa-id">ID do Database - Tipos de Despesa</Label>
            <Input
              id="tipo-despesa-id"
              placeholder="Ex: 301234568"
              value={tipoDespesaId}
              onChange={(e) => setTipoDespesaId(e.target.value)}
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground">
              O ID do database de Tipos de Despesa no Pipefy
            </p>
          </div>

          <div className="pt-4">
            <Button
              onClick={handleSync}
              disabled={loading || !centroCustoId || !tipoDespesaId}
              className="w-full"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              {loading ? "Sincronizando..." : "Sincronizar Databases"}
            </Button>
          </div>

          <div className="pt-2 text-xs text-muted-foreground border-t">
            <p className="font-semibold mb-1">Importante:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Certifique-se de que o PIPEFY_TOKEN está configurado no arquivo .env.local</li>
              <li>Os IDs dos databases podem ser encontrados na URL do database no Pipefy</li>
              <li>A sincronização irá atualizar ou criar os registros baseado no pipefy_database_id</li>
              <li>Após a sincronização, os relacionamentos na tabela solis_orcamento serão atualizados automaticamente</li>
            </ul>
          </div>

          <div className="pt-4 text-xs text-muted-foreground border-t">
            <p className="font-semibold mb-1">O que acontece ao clicar em Sincronizar Databases:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>O sistema envia os IDs informados (Centros de Custo e Tipos de Despesa) para a Edge Function no Supabase.</li>
              <li>A função busca todos os registros desses dois databases no Pipefy (com paginação).</li>
              <li>Para cada registro de Centros de Custo: lê nome, código e descrição e faz upsert em solis_centros_custo (chave: pipefy_database_id).</li>
              <li>Para cada registro de Tipos de Despesa: lê nome, código, categoria e descrição e faz upsert em solis_tipos_despesa (chave: pipefy_database_id).</li>
              <li>É executado o RPC atualizar_relacionamentos_orcamento para preencher centro_custo_id e tipo_despesa_id na tabela solis_orcamento (match por texto com os dados importados).</li>
              <li>A tela exibe quantos centros e tipos foram importados e se houve erros.</li>
            </ol>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Sincronizar Database de Tipos de Despesa</CardTitle>
          <CardDescription>
            Alimenta o database de tipos no Pipefy com base no orçamento do ano selecionado, incluindo N1/N2/N3 e vínculo de tipo pai.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="tipos-database-sync-id">ID do Database - Tipos (sincronização)</Label>
            <Input
              id="tipos-database-sync-id"
              placeholder="Ex: 306808314"
              value={tiposDatabaseSyncId}
              onChange={(e) => setTiposDatabaseSyncId(e.target.value)}
              disabled={loadingTipos}
            />
            <p className="text-xs text-muted-foreground">
              Database único com colunas como: Nome Tipo, Tipo Pai, Nível e Chave Externa.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tipos-ano">Ano do orçamento</Label>
            <Input
              id="tipos-ano"
              placeholder="Ex: 2026"
              value={tiposAno}
              onChange={(e) => setTiposAno(e.target.value)}
              disabled={loadingTipos}
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="allow-create-tipos">Permitir criação de novos tipos</Label>
              <p className="text-xs text-muted-foreground">
                Quando desativado, atualiza apenas registros já existentes no Pipefy.
              </p>
            </div>
            <Switch
              id="allow-create-tipos"
              checked={allowCreateTipos}
              onCheckedChange={setAllowCreateTipos}
              disabled={loadingTipos || loadingTiposRealizado}
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="allow-deactivate-tipos">Desativar tipos ausentes no ano</Label>
              <p className="text-xs text-muted-foreground">
                Move para status de concluído os registros que não aparecem no orçamento do ano.
              </p>
            </div>
            <Switch
              id="allow-deactivate-tipos"
              checked={allowDeactivateTipos}
              onCheckedChange={setAllowDeactivateTipos}
              disabled={loadingTipos || loadingTiposRealizado}
            />
          </div>

          <Button
            onClick={handleSyncTipos}
            disabled={loadingTipos || loadingTiposRealizado || !tiposDatabaseSyncId || !tiposAno}
            className="w-full"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loadingTipos ? "animate-spin" : ""}`} />
            {loadingTipos ? "Sincronizando Tipos..." : "Sincronizar Tipos de Despesa"}
          </Button>

          <Button
            onClick={handleSyncTiposRealizadoSemOrcamento}
            disabled={loadingTipos || loadingTiposRealizado || !tiposDatabaseSyncId || !tiposAno}
            variant="outline"
            className="w-full"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loadingTiposRealizado ? "animate-spin" : ""}`} />
            {loadingTiposRealizado
              ? "Sincronizando Tipos do Realizado..."
              : "Sincronizar Tipos do Realizado sem Orçamento"}
          </Button>

          {tiposResult && (
            <div className="rounded-md border p-4 text-sm space-y-2">
              <div><span className="font-semibold">Ano:</span> {tiposResult.ano ?? "—"}</div>
              <div><span className="font-semibold">Origem:</span> {tiposResult.sourceMode === "realizado_sem_orcamento" ? "Realizado sem orçamento" : "Orçamento"}</div>
              <div><span className="font-semibold">Tipos na origem:</span> {tiposResult.sourceCount ?? 0}</div>
              <div><span className="font-semibold">Criados:</span> {tiposResult.created ?? 0}</div>
              <div><span className="font-semibold">Atualizados:</span> {tiposResult.updated ?? 0}</div>
              <div><span className="font-semibold">Desativados:</span> {tiposResult.deactivated ?? 0}</div>
              {Array.isArray(tiposResult.updatedItems) && tiposResult.updatedItems.length > 0 && (
                <details className="rounded border p-2">
                  <summary className="cursor-pointer font-medium">Ver tipos atualizados ({tiposResult.updatedItems.length})</summary>
                  <ul className="mt-2 text-xs text-muted-foreground space-y-1 max-h-36 overflow-auto">
                    {tiposResult.updatedItems.map((item: string, idx: number) => (
                      <li key={`upd-${idx}`} className="list-disc list-inside break-all">{item}</li>
                    ))}
                  </ul>
                </details>
              )}
              {Array.isArray(tiposResult.createdItems) && tiposResult.createdItems.length > 0 && (
                <details className="rounded border p-2">
                  <summary className="cursor-pointer font-medium">Ver tipos criados ({tiposResult.createdItems.length})</summary>
                  <ul className="mt-2 text-xs text-muted-foreground space-y-1 max-h-36 overflow-auto">
                    {tiposResult.createdItems.map((item: string, idx: number) => (
                      <li key={`crt-${idx}`} className="list-disc list-inside break-all">{item}</li>
                    ))}
                  </ul>
                </details>
              )}
              {tiposResult?.errors?.length > 0 && (
                <div>
                  <p className="font-semibold text-red-600 mb-1">Erros:</p>
                  <ul className="text-xs text-muted-foreground space-y-1 max-h-36 overflow-auto">
                    {tiposResult.errors.map((error: string, idx: number) => (
                      <li key={idx} className="list-disc list-inside break-all">{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {result && (
        <div className="space-y-4">
          {/* Resultado Centros de Custo */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {result.centrosCusto.errors.length === 0 ? (
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-yellow-500" />
                )}
                Centros de Custo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p className="text-sm">
                  <span className="font-semibold">Importados:</span> {result.centrosCusto.imported} registros
                </p>
                {result.centrosCusto.errors.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-red-600 mb-1">Erros:</p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      {result.centrosCusto.errors.map((error, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <XCircle className="h-3 w-3 text-red-500 mt-0.5 flex-shrink-0" />
                          {error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Resultado Tipos de Despesa */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {result.tiposDespesa.errors.length === 0 ? (
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-yellow-500" />
                )}
                Tipos de Despesa
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p className="text-sm">
                  <span className="font-semibold">Importados:</span> {result.tiposDespesa.imported} registros
                </p>
                {result.tiposDespesa.errors.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-red-600 mb-1">Erros:</p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      {result.tiposDespesa.errors.map((error, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <XCircle className="h-3 w-3 text-red-500 mt-0.5 flex-shrink-0" />
                          {error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Vincular centros de custo (planilha → Pipefy) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Vincular centros de custo (planilha → Pipefy)
          </CardTitle>
          <CardDescription>
            Vincule cada par <strong>Área / Subárea</strong> (colunas O e P da planilha) ao centro de custo do Pipefy. Use também o bloco legado para &quot;centro de resultado&quot; quando não houver área. Depois, clique em &quot;Aplicar vínculos ao orçamento&quot;.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={loadVinculos}
              disabled={loadingVinculos}
            >
              {loadingVinculos ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span className="ml-2">Atualizar lista</span>
            </Button>
            <Button
              onClick={applyVinculosToOrcamento}
              disabled={applyingRpc || loadingVinculos || (paresAreaSubArea.length === 0 && centrosPlanilha.length === 0)}
            >
              {applyingRpc ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Link2 className="h-4 w-4 mr-2" />}
              Aplicar vínculos ao orçamento
            </Button>
          </div>

          {loadingVinculos ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* Tabela por área e subárea */}
              {paresAreaSubArea.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Por área e subárea (estrutura nova)</h4>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Área</TableHead>
                          <TableHead>Subárea</TableHead>
                          <TableHead className="w-24 text-center">Linhas</TableHead>
                          <TableHead>Vinculado a</TableHead>
                          <TableHead>Vincular ao centro (Pipefy)</TableHead>
                          <TableHead className="w-[180px]">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paresAreaSubArea.map(({ area, sub_area, count }) => {
                          const key = keyAreaSubArea(area, sub_area);
                          const vinculadoNome = getVinculadoArea(area, sub_area);
                          const selectedId = selectedMap[key] ?? "";
                          return (
                            <TableRow key={key}>
                              <TableCell className="font-medium">{area}</TableCell>
                              <TableCell>{sub_area}</TableCell>
                              <TableCell className="text-center">{count}</TableCell>
                              <TableCell className="text-muted-foreground">
                                {vinculadoNome ? (
                                  <span className="text-green-600 dark:text-green-400">{vinculadoNome}</span>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <Select
                                  value={selectedId || "__none__"}
                                  onValueChange={(v) => setSelectedMap((prev) => ({ ...prev, [key]: v === "__none__" ? "" : v }))}
                                >
                                  <SelectTrigger className="w-full max-w-[280px]">
                                    <SelectValue placeholder="Selecione o centro" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__none__">— Nenhum —</SelectItem>
                                    {centrosCusto.map((c) => (
                                      <SelectItem key={c.id} value={c.id}>
                                        {c.nome} {c.nivel ? `(${c.nivel})` : ""}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="default"
                                    disabled={!selectedId || savingKey === key}
                                    onClick={() => selectedId && saveMappingArea(area, sub_area, selectedId)}
                                  >
                                    {savingKey === key ? <Loader2 className="h-3 w-3 animate-spin" /> : "Salvar"}
                                  </Button>
                                  {vinculadoNome && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-destructive hover:text-destructive"
                                      disabled={removingKey === key}
                                      onClick={() => removeMappingArea(area, sub_area)}
                                      title="Remover vínculo"
                                    >
                                      {removingKey === key ? <Loader2 className="h-3 w-3 animate-spin" /> : <Unlink className="h-3 w-3" />}
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Tabela legado: centro_resultado */}
              {centrosPlanilha.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Por centro de resultado (legado)</h4>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nome na planilha (centro_resultado)</TableHead>
                          <TableHead className="w-24 text-center">Linhas</TableHead>
                          <TableHead>Vinculado a</TableHead>
                          <TableHead>Vincular ao centro (Pipefy)</TableHead>
                          <TableHead className="w-[180px]">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {centrosPlanilha.map(({ nome, count }) => {
                          const vinculadoNome = getVinculadoNome(nome);
                          const selectedId = selectedMapLegado[nome] ?? "";
                          return (
                            <TableRow key={nome}>
                              <TableCell className="font-medium">{nome}</TableCell>
                              <TableCell className="text-center">{count}</TableCell>
                              <TableCell className="text-muted-foreground">
                                {vinculadoNome ? (
                                  <span className="text-green-600 dark:text-green-400">{vinculadoNome}</span>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <Select
                                  value={selectedId || "__none__"}
                                  onValueChange={(v) => setSelectedMapLegado((prev) => ({ ...prev, [nome]: v === "__none__" ? "" : v }))}
                                >
                                  <SelectTrigger className="w-full max-w-[280px]">
                                    <SelectValue placeholder="Selecione o centro" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__none__">— Nenhum —</SelectItem>
                                    {centrosCusto.map((c) => (
                                      <SelectItem key={c.id} value={c.id}>
                                        {c.nome} {c.nivel ? `(${c.nivel})` : ""}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="default"
                                    disabled={!selectedId || savingKey === "legado:" + nome}
                                    onClick={() => selectedId && saveMappingLegado(nome, selectedId)}
                                  >
                                    {savingKey === "legado:" + nome ? <Loader2 className="h-3 w-3 animate-spin" /> : "Salvar"}
                                  </Button>
                                  {vinculadoNome && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-destructive hover:text-destructive"
                                      disabled={removingKey === "legado:" + nome}
                                      onClick={() => removeMappingLegado(nome)}
                                      title="Remover vínculo"
                                    >
                                      {removingKey === "legado:" + nome ? <Loader2 className="h-3 w-3 animate-spin" /> : <Unlink className="h-3 w-3" />}
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {paresAreaSubArea.length === 0 && centrosPlanilha.length === 0 && (
                <p className="text-sm text-muted-foreground py-4">
                  Nenhum par área/subárea nem centro de resultado na tabela solis_orcamento. Importe dados da planilha na página de Orçamento primeiro.
                </p>
              )}
            </>
          )}

          <p className="text-xs text-muted-foreground">
            O RPC <code className="bg-muted px-1 rounded">atualizar_relacionamentos_orcamento</code> usa primeiro o mapeamento por (área, subárea) e por centro de resultado; depois match automático com normalização (ex.: &quot; E &quot; → &quot; &amp; &quot;). Aplicar vínculos atualiza <code>centro_custo_id</code> e <code>tipo_despesa_id</code> na tabela solis_orcamento.
          </p>
        </CardContent>
      </Card>

      {/* Sincronização de Orçamento */}
      <Card>
        <CardHeader>
          <CardTitle>Sincronizar Database de Orçamento</CardTitle>
          <CardDescription>
            Popule o database de orçamento no Pipefy com os dados da view agregada. Este database será usado para consultar valores orçados quando cards forem criados.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="orcamento-database-id">ID do Database - Orçamento</Label>
            <Input
              id="orcamento-database-id"
              placeholder="Ex: 306808641"
              value={orcamentoDatabaseId}
              onChange={(e) => setOrcamentoDatabaseId(e.target.value)}
              disabled={loadingOrcamento}
            />
            <p className="text-xs text-muted-foreground">
              O ID do database de Orçamento no Pipefy (espelho da view solis_vw_orcamento_por_tipo_centro)
            </p>
          </div>

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="allow-create-orcamento">Permitir criação de registros</Label>
              <p className="text-xs text-muted-foreground">
                Ative para criar no Pipefy os registros que ainda não existem (database vazio ou parcial). Desative para apenas atualizar existentes.
              </p>
            </div>
            <Switch
              id="allow-create-orcamento"
              checked={allowCreateOrcamento}
              onCheckedChange={setAllowCreateOrcamento}
              disabled={loadingOrcamento}
            />
          </div>

          <div className="rounded-lg border p-4">
            <p className="text-sm font-medium">Modo da sincronização: V2 (fixo)</p>
            <p className="text-xs text-muted-foreground mt-1">
              A sincronização de orçamento usa apenas a estrutura nova (view v2 por subtipo).
            </p>
          </div>

          <div className="space-y-2">
            <Button
              onClick={handleSyncOrcamentoData}
              disabled={loadingOrcamento || !orcamentoDatabaseId || !centroCustoId || !tipoDespesaId}
              className="w-full"
              title={allowCreateOrcamento ? "Sincronizar e criar registros faltantes no Pipefy" : "Sincronizar (apenas atualizar registros existentes)"}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loadingOrcamento ? "animate-spin" : ""}`} />
              {loadingOrcamento ? "Sincronizando..." : "Sincronizar Dados"}
            </Button>
          </div>

          <div className="pt-2 text-xs text-muted-foreground border-t">
            <p className="font-semibold mb-1">Como funciona:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Certifique-se de ter os IDs dos databases de Centros de Custo e Tipos de Despesa preenchidos acima</li>
              <li>Clique em "Sincronizar Dados" para popular o database com os dados da view</li>
              <li>O database será populado com dados agregados da view (ano, centro, tipo, valor total, etc.)</li>
              <li>Os campos "Área de Negócio" e "Tipo Despesa" são connectors e precisam dos IDs dos databases correspondentes</li>
              <li>Os campos são mapeados automaticamente usando os IDs conhecidos do database</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {(orcamentoResult || viewOrcamentoCount != null) && (
        <Card>
          <CardHeader>
            <CardTitle>Estrutura do Database de Orçamento</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-semibold">Tipo:</span> {orcamentoResult?.isPipe ? "Pipe" : "Database/Table"}
              </div>
              <div>
                <span className="font-semibold">Nome:</span> {orcamentoResult?.tableName ?? "N/A"}
              </div>
              <div>
                <span className="font-semibold">Registros na View:</span> {orcamentoResult?.totalRecordsInView ?? viewOrcamentoCount ?? "—"}
              </div>
              <div>
                <span className="font-semibold">Registros Existentes:</span> {orcamentoResult?.existingRecordsCount ?? "—"}
              </div>
              <div>
                <span className="font-semibold">Campos Encontrados:</span> {orcamentoResult?.availableFields?.length ?? 0}
              </div>
              {orcamentoResult?.synced !== undefined && (
                <>
                  <div>
                    <span className="font-semibold">Registros Sincronizados:</span> {orcamentoResult.synced}
                  </div>
                  {(orcamentoResult?.totalErrors ?? 0) > 0 && (
                    <div>
                      <span className="font-semibold text-red-600">Erros:</span> {orcamentoResult.totalErrors}
                    </div>
                  )}
                </>
              )}
            </div>
            
            {orcamentoResult?.errors && orcamentoResult.errors.length > 0 && (
              <div className="p-4 rounded border border-red-500 bg-red-50 dark:bg-red-900/20">
                <p className="text-sm font-semibold text-red-800 dark:text-red-200 mb-2">Erros durante sincronização:</p>
                <ul className="text-xs text-red-700 dark:text-red-300 space-y-1 max-h-40 overflow-auto">
                  {orcamentoResult.errors.map((error: string, idx: number) => (
                    <li key={idx} className="list-disc list-inside break-all">{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {orcamentoResult?.logs && orcamentoResult.logs.length > 0 && (
              <details className="rounded border bg-muted/50">
                <summary className="p-3 cursor-pointer font-medium text-sm">Logs da sincronização ({orcamentoResult.logs.length} linhas)</summary>
                <pre className="p-3 text-xs overflow-auto max-h-60 border-t whitespace-pre-wrap break-words">
                  {orcamentoResult.logs.join("\n")}
                </pre>
              </details>
            )}

            {orcamentoResult?.availableFields && orcamentoResult.availableFields.length > 0 && (
              <div>
                <p className="font-semibold mb-2">Campos Disponíveis no Database:</p>
                <div className="space-y-2">
                  {orcamentoResult.availableFields.map((field: any, idx: number) => (
                    <div key={idx} className="p-3 rounded border text-xs">
                      <div className="font-semibold text-sm mb-1">
                        {field.label || field.name || `Campo ${idx + 1}`}
                        {field.source && <span className="ml-2 text-xs text-muted-foreground">({field.source})</span>}
                      </div>
                      <div className="text-muted-foreground space-y-1">
                        <div><span className="font-medium">ID:</span> {field.id || field.fieldId || "N/A"}</div>
                        {(field.internalId ?? field.internal_id) && <div><span className="font-medium">Internal ID:</span> {field.internalId ?? field.internal_id}</div>}
                        <div><span className="font-medium">Tipo:</span> {field.type || "N/A"}</div>
                        {field.required !== undefined && <div><span className="font-medium">Obrigatório:</span> {field.required ? "Sim" : "Não"}</div>}
                        {field.value !== undefined && <div><span className="font-medium">Valor exemplo:</span> {String(field.value || "(vazio)")}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {(!orcamentoResult?.availableFields || orcamentoResult.availableFields.length === 0) && (
              <div className="p-4 rounded border border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  ⚠️ Não foi possível identificar os campos do database. Isso pode acontecer se:
                </p>
                <ul className="text-xs text-yellow-700 dark:text-yellow-300 mt-2 list-disc list-inside space-y-1">
                  <li>O database está vazio (sem registros)</li>
                  <li>A API do Pipefy não retorna a estrutura de campos diretamente</li>
                  <li>O ID do database está incorreto</li>
                </ul>
                <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-2">
                  <strong>Sugestão:</strong> Clique em &quot;Sincronizar Dados&quot; para carregar a estrutura do database no Pipefy. Se o database estiver vazio, crie um registro de teste e tente novamente.
                </p>
              </div>
            )}

            {orcamentoResult?.sampleViewData && (
              <div>
                <p className="font-semibold mb-2">Dados da View (Exemplo):</p>
                <pre className="bg-muted p-4 rounded text-xs overflow-auto">
                  {JSON.stringify(orcamentoResult?.sampleViewData, null, 2)}
                </pre>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

